import { useMemo } from "react";
import { 
  Lightbulb, 
  Plus, 
  Minus, 
  TrendingUp, 
  TrendingDown,
  Zap,
  ChevronRight
} from "lucide-react";
import { cn } from "../../lib/utils";

// Threshold for showing suggestions (in absolute values)
const THRESHOLDS = {
  calories: 100,  // Show suggestion if difference > 100 kcal
  protein: 10,    // > 10g protein
  carbs: 15,      // > 15g carbs
  fat: 8          // > 8g fat
};

// Priority macros for fitness goals
const MACRO_PRIORITY = ['protein', 'calories', 'carbs', 'fat'];

// Generate smart suggestions based on macro differences
const generateSuggestions = (targets, actuals, foodsLibrary, currentMealItems) => {
  const suggestions = [];
  
  if (!targets || !actuals || !foodsLibrary) return suggestions;
  
  const diffs = {
    calories: (targets.calories_target || 0) - actuals.calories,
    protein: (targets.protein_target || 0) - actuals.protein,
    carbs: (targets.carbs_target || 0) - actuals.carbs,
    fat: (targets.fat_target || 0) - actuals.fat
  };

  // Get all foods from library as flat array
  const allFoods = Object.values(foodsLibrary).flat();
  
  // Get foods currently used in diet (for priority suggestions)
  const usedFoodIds = new Set();
  currentMealItems.forEach(item => {
    if (item.food_id && item.food_id !== 'custom') {
      usedFoodIds.add(item.food_id);
    }
  });
  
  const usedFoods = allFoods.filter(f => usedFoodIds.has(f.id));

  // Helper: Find best food to adjust a macro
  const findBestFoodForMacro = (macro, isDeficit) => {
    // Map macro names to food properties
    const macroMap = {
      protein: 'protein_per_100',
      carbs: 'carbs_per_100',
      fat: 'fat_per_100',
      calories: 'calories_per_100'
    };
    
    const prop = macroMap[macro];
    if (!prop) return null;

    // First, try foods already in the diet
    if (usedFoods.length > 0) {
      const sortedUsed = [...usedFoods]
        .filter(f => (f[prop] || 0) > 5) // Filter foods with meaningful macro content
        .sort((a, b) => (b[prop] || 0) - (a[prop] || 0));
      
      if (sortedUsed.length > 0) {
        return { food: sortedUsed[0], isUsed: true };
      }
    }

    // Fallback to library
    // For protein deficit: prioritize protein sources
    // For carb deficit: prioritize carb sources
    // For fat deficit: prioritize fat sources
    let categoryPriority = [];
    if (macro === 'protein') {
      categoryPriority = ['proteinas'];
    } else if (macro === 'carbs') {
      categoryPriority = ['carbohidratos', 'frutas'];
    } else if (macro === 'fat') {
      categoryPriority = ['grasas_saludables'];
    }

    // Filter and sort
    let candidates = allFoods.filter(f => {
      if (categoryPriority.length > 0) {
        return categoryPriority.includes(f.category) && (f[prop] || 0) > 5;
      }
      return (f[prop] || 0) > 10;
    });

    candidates.sort((a, b) => (b[prop] || 0) - (a[prop] || 0));
    
    return candidates.length > 0 ? { food: candidates[0], isUsed: false } : null;
  };

  // Helper: Calculate amount needed to cover deficit/surplus
  const calculateAmount = (food, macro, diff) => {
    const macroMap = {
      protein: 'protein_per_100',
      carbs: 'carbs_per_100',
      fat: 'fat_per_100',
      calories: 'calories_per_100'
    };
    
    const per100 = food[macroMap[macro]] || 0;
    if (per100 <= 0) return 0;
    
    // Calculate grams needed to cover the difference
    const gramsNeeded = Math.abs(diff) / per100 * 100;
    
    // Round to nice numbers (5g increments for small, 10g for larger)
    if (gramsNeeded < 50) {
      return Math.round(gramsNeeded / 5) * 5;
    }
    return Math.round(gramsNeeded / 10) * 10;
  };

  // Generate suggestions for each macro
  MACRO_PRIORITY.forEach(macro => {
    const diff = diffs[macro];
    const threshold = THRESHOLDS[macro];
    
    if (Math.abs(diff) < threshold) return;
    
    const isDeficit = diff > 0; // Positive diff means we need MORE
    const result = findBestFoodForMacro(macro, isDeficit);
    
    if (!result) return;
    
    const { food, isUsed } = result;
    const amount = calculateAmount(food, macro, diff);
    
    if (amount < 5) return; // Skip tiny adjustments
    
    // Create suggestion
    const macroLabels = {
      calories: 'Calorías',
      protein: 'Proteína',
      carbs: 'Carbohidratos',
      fat: 'Grasas'
    };
    
    const macroColors = {
      calories: 'text-[#D4AF37]',
      protein: 'text-red-400',
      carbs: 'text-yellow-400',
      fat: 'text-blue-400'
    };

    suggestions.push({
      id: `${macro}-${food.id}`,
      type: isDeficit ? 'add' : 'reduce',
      macro,
      macroLabel: macroLabels[macro],
      macroColor: macroColors[macro],
      diff: Math.round(Math.abs(diff)),
      food,
      amount,
      isUsedFood: isUsed,
      priority: MACRO_PRIORITY.indexOf(macro),
      // Pre-calculated impact
      impact: {
        calories: Math.round((food.calories_per_100 || 0) * amount / 100),
        protein: Math.round((food.protein_per_100 || 0) * amount / 100 * 10) / 10,
        carbs: Math.round((food.carbs_per_100 || 0) * amount / 100 * 10) / 10,
        fat: Math.round((food.fat_per_100 || 0) * amount / 100 * 10) / 10
      }
    });
  });

  // Sort by priority
  suggestions.sort((a, b) => a.priority - b.priority);
  
  return suggestions.slice(0, 4); // Max 4 suggestions
};

// Suggestion Card Component
const SuggestionCard = ({ suggestion, onApply }) => {
  const isAdd = suggestion.type === 'add';
  
  return (
    <button
      onClick={() => onApply(suggestion)}
      className={cn(
        "w-full p-3 rounded-lg border text-left transition-all group",
        "hover:scale-[1.02] active:scale-[0.98]",
        isAdd 
          ? "bg-green-500/5 border-green-500/20 hover:border-green-500/40" 
          : "bg-orange-500/5 border-orange-500/20 hover:border-orange-500/40"
      )}
    >
      <div className="flex items-start gap-3">
        {/* Icon */}
        <div className={cn(
          "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
          isAdd ? "bg-green-500/10" : "bg-orange-500/10"
        )}>
          {isAdd ? (
            <Plus className="w-4 h-4 text-green-400" />
          ) : (
            <Minus className="w-4 h-4 text-orange-400" />
          )}
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Problem description */}
          <div className="flex items-center gap-2 mb-1">
            <span className={cn("text-xs font-medium", suggestion.macroColor)}>
              {suggestion.macroLabel}
            </span>
            <span className="text-[#666] text-[10px]">
              {isAdd ? `faltan ${suggestion.diff}` : `sobran ${suggestion.diff}`}
              {suggestion.macro === 'calories' ? ' kcal' : 'g'}
            </span>
          </div>
          
          {/* Suggestion */}
          <div className="text-white text-sm font-medium">
            {isAdd ? 'Añadir' : 'Reducir'} {suggestion.amount}
            {suggestion.food.unit === 'uds' ? ' uds' : 'g'} {suggestion.food.name}
          </div>
          
          {/* Impact preview */}
          <div className="flex items-center gap-2 mt-1 text-[10px]">
            <span className="text-[#555]">Impacto:</span>
            <span className="text-[#D4AF37]">{isAdd ? '+' : '-'}{suggestion.impact.calories} kcal</span>
            <span className="text-red-400">{isAdd ? '+' : '-'}{suggestion.impact.protein}P</span>
            <span className="text-yellow-400">{isAdd ? '+' : '-'}{suggestion.impact.carbs}C</span>
          </div>
          
          {/* Used food badge */}
          {suggestion.isUsedFood && (
            <span className="inline-block mt-1 px-2 py-0.5 text-[9px] bg-[#D4AF37]/10 text-[#D4AF37] rounded">
              Ya en la dieta
            </span>
          )}
        </div>
        
        {/* Apply arrow */}
        <div className={cn(
          "w-6 h-6 rounded flex items-center justify-center transition-colors",
          "bg-[#2A2A2A] group-hover:bg-[#3A3A3A]"
        )}>
          <ChevronRight className="w-4 h-4 text-[#666] group-hover:text-white" />
        </div>
      </div>
    </button>
  );
};

// Main Component
const MacroSuggestions = ({ 
  targets,           // { calories_target, protein_target, carbs_target, fat_target }
  actuals,           // { calories, protein, carbs, fat }
  foodsLibrary,      // Foods grouped by category
  currentMealItems,  // All items from all meals (for prioritizing used foods)
  onApplySuggestion  // Callback when user clicks to apply
}) => {
  const suggestions = useMemo(() => 
    generateSuggestions(targets, actuals, foodsLibrary, currentMealItems),
    [targets, actuals, foodsLibrary, currentMealItems]
  );

  if (suggestions.length === 0) {
    // Check if we're close to targets
    const isOnTarget = targets && actuals && 
      Math.abs((targets.calories_target || 0) - actuals.calories) < THRESHOLDS.calories &&
      Math.abs((targets.protein_target || 0) - actuals.protein) < THRESHOLDS.protein;
    
    if (isOnTarget) {
      return (
        <div className="p-4 bg-green-500/5 rounded-lg border border-green-500/20">
          <div className="flex items-center gap-2 text-green-400">
            <Zap className="w-4 h-4" />
            <span className="text-sm font-medium">¡Macros equilibrados!</span>
          </div>
          <p className="text-[#666] text-xs mt-1">
            La dieta está dentro de los objetivos establecidos.
          </p>
        </div>
      );
    }
    
    return null;
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
          <Lightbulb className="w-4 h-4 text-[#D4AF37]" />
        </div>
        <div>
          <h4 className="text-white text-sm font-semibold">Sugerencias de Ajuste</h4>
          <p className="text-[#666] text-[10px]">Click para aplicar • El coach mantiene el control</p>
        </div>
      </div>

      {/* Suggestions */}
      <div className="space-y-2">
        {suggestions.map(suggestion => (
          <SuggestionCard
            key={suggestion.id}
            suggestion={suggestion}
            onApply={onApplySuggestion}
          />
        ))}
      </div>
      
      {/* Disclaimer */}
      <p className="text-[#444] text-[9px] text-center">
        Las sugerencias son orientativas. Ajusta según las necesidades del cliente.
      </p>
    </div>
  );
};

export default MacroSuggestions;
